/**
 * Utility functions for managing conversation ID persistence in localStorage
 * Part of AUTO-670: React Query Hooks for RAG Operations
 */

const STORAGE_KEY_PREFIX = 'documentRag_conversation_';

/**
 * Get the localStorage key for a given submission ID
 */
function getStorageKey(submissionId: string): string {
  return `${STORAGE_KEY_PREFIX}${submissionId}`;
}

/**
 * Retrieve the conversation ID for a given submission from localStorage
 *
 * @param submissionId - The submission ID to retrieve the conversation ID for
 * @returns The conversation ID if found, null otherwise
 */
export function getConversationId(submissionId: string): string | null {
  if (!submissionId) {
    return null;
  }

  try {
    const key = getStorageKey(submissionId);
    const stored = localStorage.getItem(key);
    return stored ? stored : null;
  } catch (error) {
    console.error(
      `Error reading conversation ID from localStorage for submission ${submissionId}:`,
      error
    );
    return null;
  }
}

/**
 * Store the conversation ID for a given submission in localStorage
 *
 * @param submissionId - The submission ID to store the conversation ID for
 * @param conversationId - The conversation ID to store
 */
export function setConversationId(
  submissionId: string,
  conversationId: string
): void {
  if (!submissionId || !conversationId) {
    return;
  }

  try {
    const key = getStorageKey(submissionId);
    localStorage.setItem(key, conversationId);
  } catch (error) {
    console.error(
      `Error storing conversation ID in localStorage for submission ${submissionId}:`,
      error
    );
  }
}

/**
 * Clear the conversation ID for a given submission from localStorage
 *
 * @param submissionId - The submission ID to clear the conversation ID for
 */
export function clearConversationId(submissionId: string): void {
  if (!submissionId) {
    return;
  }

  try {
    const key = getStorageKey(submissionId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error(
      `Error clearing conversation ID from localStorage for submission ${submissionId}:`,
      error
    );
  }
}

